import { useEffect, useState, type FormEventHandler } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Eye, Save, Trash2 } from 'lucide-react';

import { ALLOWED_ATTACHMENT_TYPES, api, ApiError } from '../api/client';
import type { Attachment, Complaint, CurrentUser } from '../api/types';
import { AttachmentDropZone } from '../components/AttachmentDropZone';
import { Field } from '../components/Field';
import { Loading } from '../components/Loading';
import { IssueSelect, OutcomeSelect, issueLabel } from '../components/IssueSelect';
import { StatusBadge, SensitiveBadge, OverdueBadge } from '../components/Badges';
import { useToast } from '../components/Toast';
import { awstLocalToUtcIso, formatAuditValue, formatDateTime, utcIsoToAwstLocal } from '../format';

const MAX_ATTACHMENT_BYTES = 25 * 1024 * 1024;

const formatFileSize = (bytes: number) =>
  bytes < 1024 * 1024 ? `${Math.ceil(bytes / 1024)} KB` : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

function NoteComposer({
  complaintId,
  onAdded,
  onError,
}: {
  complaintId: number;
  onAdded: () => void;
  onError: (message: string) => void;
}) {
  const [note, setNote] = useState('');
  const mutation = useMutation({
    mutationFn: () => api.addNote(complaintId, note.trim()),
    onSuccess: () => {
      setNote('');
      onAdded();
    },
    onError: (err: unknown) => onError(err instanceof Error ? err.message : 'Could not add note'),
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        if (note.trim()) mutation.mutate();
      }}
      style={{ marginBottom: 14 }}
    >
      <Field label="Add a note" htmlFor="new_note">
        <textarea
          id="new_note"
          value={note}
          placeholder="Record a call, an internal note, anything worth keeping with the case… (max 5,000 characters)"
          maxLength={5000}
          onChange={(e) => setNote(e.target.value)}
        />
      </Field>
      <button
        type="submit"
        className="btn small"
        disabled={mutation.isPending || !note.trim()}
        style={{ marginTop: 6 }}
      >
        {mutation.isPending ? 'Saving…' : 'Add note'}
      </button>
    </form>
  );
}

function AttachmentUploader({
  complaintId,
  onUploaded,
  onError,
}: {
  complaintId: number;
  onUploaded: () => void;
  onError: (message: string) => void;
}) {
  const [uploading, setUploading] = useState(false);

  const handleFiles = async (picked: File[]) => {
    const valid: File[] = [];
    for (const file of picked) {
      if (!ALLOWED_ATTACHMENT_TYPES.includes(file.type)) {
        onError(`${file.name}: this file type is not allowed`);
      } else if (file.size > MAX_ATTACHMENT_BYTES) {
        onError(`${file.name}: files must be ${MAX_ATTACHMENT_BYTES / (1024 * 1024)} MB or smaller`);
      } else {
        valid.push(file);
      }
    }
    if (valid.length === 0) return;

    setUploading(true);
    let failed = 0;
    for (const file of valid) {
      try {
        await api.uploadAttachment(complaintId, file);
      } catch {
        failed += 1;
      }
    }
    setUploading(false);
    if (failed > 0) onError(`${failed} file(s) could not be uploaded`);
    onUploaded();
  };

  return (
    <div style={{ marginBottom: 14 }}>
      <AttachmentDropZone
        onFiles={(files) => void handleFiles(files)}
        hint="PDF, Word, Excel, images — up to 25 MB each"
        disabled={uploading}
      />
      {uploading && <span className="faint" style={{ fontSize: 12 }}>Uploading…</span>}
    </div>
  );
}

function AttachmentRow({
  complaintId,
  attachment,
  canDelete,
  onError,
  onDeleted,
  onNoteSaved,
}: {
  complaintId: number;
  attachment: Attachment;
  canDelete: boolean;
  onError: (message: string) => void;
  onDeleted: () => void;
  onNoteSaved: () => void;
}) {
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [noteDraft, setNoteDraft] = useState(attachment.note ?? '');

  const deleteMutation = useMutation({
    mutationFn: () => api.deleteAttachment(complaintId, attachment.id),
    onSuccess: onDeleted,
    onError: (err: unknown) => onError(err instanceof Error ? err.message : 'Could not delete attachment'),
  });

  const noteMutation = useMutation({
    mutationFn: () => api.updateAttachmentNote(complaintId, attachment.id, noteDraft.trim()),
    onSuccess: onNoteSaved,
    onError: (err: unknown) => onError(err instanceof Error ? err.message : 'Could not save note'),
  });

  const openWith = async (disposition: 'inline' | 'attachment', setBusy: (busy: boolean) => void) => {
    setBusy(true);
    try {
      const { url } = await api.getAttachmentDownloadUrl(complaintId, attachment.id, disposition);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Could not get download link');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="log-row">
      <span className="log-time">{formatDateTime(attachment.uploaded_at)}</span>
      <span className="log-actor">{attachment.uploaded_by}</span>
      <span style={{ minWidth: 160 }}>
        {attachment.filename} <span className="faint">({formatFileSize(attachment.file_size)})</span>
      </span>
      <input
        value={noteDraft}
        placeholder="Add a note (optional, max 1000 characters)"
        maxLength={1000}
        onChange={(e) => setNoteDraft(e.target.value)}
        style={{ flex: 1 }}
      />
      {noteDraft.trim() !== (attachment.note ?? '') && (
        <button type="button" className="btn small" onClick={() => noteMutation.mutate()} disabled={noteMutation.isPending}>
          {noteMutation.isPending ? 'Saving…' : 'Save note'}
        </button>
      )}
      <button
        type="button"
        className="btn small"
        onClick={() => openWith('inline', setPreviewing)}
        disabled={previewing}
        aria-label="Preview"
        title={previewing ? 'Preparing…' : 'Preview'}
      >
        <Eye size={14} aria-hidden />
      </button>
      <button
        type="button"
        className="btn small"
        onClick={() => openWith('attachment', setDownloading)}
        disabled={downloading}
        aria-label="Download"
        title={downloading ? 'Preparing…' : 'Download'}
      >
        <Download size={14} aria-hidden />
      </button>
      {canDelete && (
        <button
          type="button"
          className="btn small danger"
          disabled={deleteMutation.isPending}
          onClick={() => {
            if (confirm(`Delete "${attachment.filename}"? This cannot be undone.`)) deleteMutation.mutate();
          }}
          aria-label="Delete"
          title="Delete"
        >
          <Trash2 size={14} aria-hidden />
        </button>
      )}
    </div>
  );
}

/** The subset of a complaint this page edits. */
type Editable = Pick<
  Complaint,
  | 'status'
  | 'first_name'
  | 'last_name'
  | 'complainant_email'
  | 'complainant_phone'
  | 'application_number'
  | 'loan_number'
  | 'assigned_to'
  | 'channel'
  | 'direct_or_rep'
  | 'representative_name'
  | 'description'
  | 'desired_outcome'
  | 'date_closed'
  | 'outcome_a'
  | 'outcome_b'
  | 'outcome_c'
  | 'monetary_remedy'
  | 'material_findings'
  | 'decision_remediation'
  | 'issue_a'
  | 'issue_b'
  | 'issue_c'
> & { is_sensitive: boolean; acknowledged: boolean; idr_response_sent: boolean };

function toEditable(c: Complaint): Editable {
  return {
    status: c.status,
    first_name: c.first_name,
    last_name: c.last_name,
    complainant_email: c.complainant_email ?? '',
    complainant_phone: c.complainant_phone ?? '',
    application_number: c.application_number ?? '',
    loan_number: c.loan_number ?? '',
    assigned_to: c.assigned_to ?? '',
    channel: c.channel,
    direct_or_rep: c.direct_or_rep,
    representative_name: c.representative_name ?? '',
    description: c.description,
    desired_outcome: c.desired_outcome ?? '',
    date_closed: c.date_closed ? utcIsoToAwstLocal(c.date_closed) : '',
    outcome_a: c.outcome_a,
    outcome_b: c.outcome_b,
    outcome_c: c.outcome_c,
    monetary_remedy: c.monetary_remedy ?? '',
    material_findings: c.material_findings ?? '',
    decision_remediation: c.decision_remediation ?? '',
    issue_a: c.issue_a,
    issue_b: c.issue_b,
    issue_c: c.issue_c,
    is_sensitive: Boolean(c.is_sensitive),
    acknowledged: Boolean(c.acknowledged_at),
    idr_response_sent: Boolean(c.idr_response_sent_at),
  };
}

export function ComplaintDetail({ user }: { user: CurrentUser }) {
  const { id } = useParams<{ id: string }>();
  const complaintId = Number(id);
  const queryClient = useQueryClient();
  const { show, element: toastElement } = useToast();

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['complaint', complaintId],
    queryFn: () => api.getComplaint(complaintId),
    enabled: Number.isInteger(complaintId),
  });

  const { data: reference } = useQuery({ queryKey: ['reference'], queryFn: api.reference, staleTime: 15 * 60_000 });

  const [form, setForm] = useState<Editable | null>(null);

  // Reset the form whenever a fresh version of the record arrives, so a
  // reload after a concurrent edit shows the other person's values.
  useEffect(() => {
    if (data) setForm(toEditable(data.complaint));
  }, [data?.complaint.row_version, data]);

  const mutation = useMutation({
    mutationFn: () => {
      if (!data || !form) throw new Error('Nothing to save');
      return api.updateComplaint(complaintId, {
        ...form,
        is_sensitive: form.is_sensitive,
        monetary_remedy: form.monetary_remedy === '' ? null : form.monetary_remedy,
        // Left blank while closing: omit the field entirely (rather than send
        // null) so the API's "closing without a date/time stamps the current
        // moment" rule fires. Only send an explicit null when an existing
        // value is being deliberately cleared. A provided value is read as
        // AWST wall-clock time (what the datetime-local input shows) and
        // converted to the UTC instant the API stores.
        date_closed:
          form.date_closed === ''
            ? data.complaint.date_closed
              ? null
              : undefined
            : awstLocalToUtcIso(form.date_closed as string),
        row_version: data.complaint.row_version,
      } as never);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['complaint', complaintId] });
      await queryClient.invalidateQueries({ queryKey: ['complaints'] });
      show('Changes saved');
    },
    onError: async (err: unknown) => {
      if (err instanceof ApiError && err.isStale) {
        await queryClient.invalidateQueries({ queryKey: ['complaint', complaintId] });
        show(err.message, 'error');
        return;
      }
      show(err instanceof Error ? err.message : 'Could not save changes', 'error');
    },
  });

  if (isLoading || !reference) return <Loading message="Loading complaint…" />;
  if (isError) return <div className="page"><div className="notice error">{(error as Error).message}</div></div>;
  if (!data || !form) return <Loading />;

  const { complaint, notes, attachments, audit, permissions } = data;
  const readOnly = !permissions.can_edit;
  const isClosed = complaint.status === 'Closed';
  const locked = readOnly || isClosed;
  const set = <K extends keyof Editable>(key: K, value: Editable[K]) =>
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));

  const setStatus = (value: Complaint['status']) => {
    if (
      value === 'Closed' &&
      form.status !== 'Closed' &&
      !confirm(
        'Close this complaint? Once closed, it becomes final — no further changes can be made to it by anyone, including administrators. You can still add notes and attachments afterwards.'
      )
    ) {
      return;
    }
    set('status', value);
  };

  const overdue =
    complaint.status === 'Open' &&
    !complaint.idr_response_sent_at &&
    new Date(complaint.idr_response_due_at) < new Date();

  const handleSave: FormEventHandler = (e) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <div className="page">
      <div className="detail-head">
        <div>
          <div className="breadcrumb">
            <Link to="/complaints">All complaints</Link> / <span className="reference">{complaint.reference}</span>
          </div>
          <h1>
            {complaint.first_name} {complaint.last_name}
          </h1>
          <p className="muted" style={{ fontSize: 13, marginTop: 2 }}>
            {[
              complaint.application_number && `App ${complaint.application_number}`,
              complaint.loan_number && `Loan ${complaint.loan_number}`,
            ]
              .filter(Boolean)
              .join(' · ') || '—'}
          </p>
          <p className="faint" style={{ fontSize: 12, marginTop: 4 }}>
            Lodged by {complaint.lodged_by} on {formatDateTime(complaint.date_received)}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          <StatusBadge status={complaint.status} />
          <SensitiveBadge sensitive={complaint.is_sensitive} />
          <OverdueBadge overdue={overdue} />
        </div>
      </div>

      {isClosed && (
        <div className="notice info">
          This complaint is closed. It is now final — nothing about it can be changed, by anyone. Notes and
          attachments can still be added below.
        </div>
      )}

      {readOnly && !isClosed && (
        <div className="notice info">
          You have read-only access to this complaint. Associates can edit only the complaints they lodged
          {user.role === 'associate' ? '' : ''} — ask an administrator if you need to make a change.
        </div>
      )}

      {complaint.status === 'Open' && (
        <div className={overdue ? 'notice warn' : 'notice info'}>
          IDR response due <strong>{formatDateTime(complaint.idr_response_due_at)}</strong> (RG 271: 30 calendar days from receipt).
          {complaint.acknowledged_at
            ? ` Acknowledged ${formatDateTime(complaint.acknowledged_at)}.`
            : ' Receipt has not yet been acknowledged to the complainant.'}
        </div>
      )}

      <form onSubmit={handleSave}>
        <div className="card">
          <h2 className="section-title">Update complaint</h2>
          <div className="grid">
            <Field label="Complaint ID" htmlFor="complaint_id" hint="The id column in v_complaints_summary">
              <input id="complaint_id" value={complaint.id} disabled />
            </Field>
            <Field label="First name" htmlFor="first_name">
              <input id="first_name" value={form.first_name} disabled={locked} onChange={(e) => set('first_name', e.target.value)} />
            </Field>
            <Field label="Last name" htmlFor="last_name">
              <input id="last_name" value={form.last_name} disabled={locked} onChange={(e) => set('last_name', e.target.value)} />
            </Field>
            <Field label="Email address" htmlFor="complainant_email">
              <input id="complainant_email" type="email" value={form.complainant_email ?? ''} disabled={locked} onChange={(e) => set('complainant_email', e.target.value)} />
            </Field>
            <Field label="Phone number" htmlFor="complainant_phone">
              <input id="complainant_phone" value={form.complainant_phone ?? ''} disabled={locked} onChange={(e) => set('complainant_phone', e.target.value)} />
            </Field>
            <Field label="Application number" htmlFor="application_number">
              <input id="application_number" value={form.application_number ?? ''} disabled={locked} onChange={(e) => set('application_number', e.target.value)} />
            </Field>
            <Field label="Loan number" htmlFor="loan_number">
              <input id="loan_number" value={form.loan_number ?? ''} disabled={locked} onChange={(e) => set('loan_number', e.target.value)} />
            </Field>
          </div>

          <div className="grid" style={{ marginTop: 14 }}>
            <Field label="Status" htmlFor="status">
              <select id="status" value={form.status} disabled={locked} onChange={(e) => setStatus(e.target.value as Complaint['status'])}>
                <option value="Open">Open</option>
                <option value="Closed">Closed</option>
              </select>
            </Field>
            {isClosed ? (
              <Field label="Date & time closed" htmlFor="date_closed">
                <input id="date_closed" value={formatDateTime(complaint.date_closed)} disabled readOnly />
              </Field>
            ) : (
              <Field
                label="Date & time closed"
                htmlFor="date_closed"
                hint="AWST — leave blank and closing stamps the current date and time automatically"
              >
                <input
                  id="date_closed"
                  type="datetime-local"
                  value={form.date_closed ?? ''}
                  disabled={locked}
                  onChange={(e) => set('date_closed', e.target.value)}
                />
              </Field>
            )}
            <Field label="Sensitive matter" htmlFor="is_sensitive">
              <select id="is_sensitive" value={form.is_sensitive ? '1' : '0'} disabled={locked} onChange={(e) => set('is_sensitive', e.target.value === '1')}>
                <option value="0">No</option>
                <option value="1">Yes</option>
              </select>
            </Field>
            <Field label="Receipt acknowledged" htmlFor="acknowledged">
              <select id="acknowledged" value={form.acknowledged ? '1' : '0'} disabled={locked} onChange={(e) => set('acknowledged', e.target.value === '1')}>
                <option value="0">Not yet</option>
                <option value="1">Yes</option>
              </select>
            </Field>
            <Field label="IDR response sent" htmlFor="idr_response_sent" hint="Stops the RG 271 clock">
              <select id="idr_response_sent" value={form.idr_response_sent ? '1' : '0'} disabled={locked} onChange={(e) => set('idr_response_sent', e.target.value === '1')}>
                <option value="0">Not yet</option>
                <option value="1">Sent</option>
              </select>
            </Field>
            <Field label="Assign to" htmlFor="assigned_to">
              <input id="assigned_to" value={form.assigned_to ?? ''} disabled={locked} onChange={(e) => set('assigned_to', e.target.value)} />
            </Field>
          </div>

          <div className="grid" style={{ marginTop: 14 }}>
            <Field label="Channel" htmlFor="channel">
              <select id="channel" value={form.channel} disabled={locked} onChange={(e) => set('channel', e.target.value)}>
                {reference.channels.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Made directly or by a representative" htmlFor="direct_or_rep">
              <select
                id="direct_or_rep"
                value={form.direct_or_rep}
                disabled={locked}
                onChange={(e) => set('direct_or_rep', e.target.value)}
              >
                <option value="Directly">Directly</option>
                <option value="Representative">Representative</option>
              </select>
            </Field>
            <Field label="Representative name" htmlFor="representative_name" hint="Required when lodged by a representative">
              <input
                id="representative_name"
                value={form.representative_name ?? ''}
                placeholder="Company or person name"
                disabled={locked || form.direct_or_rep !== 'Representative'}
                onChange={(e) => set('representative_name', e.target.value)}
              />
            </Field>
          </div>

          <div className="grid full" style={{ marginTop: 14 }}>
            <Field label="Issue 1" htmlFor="issue_a">
              <IssueSelect id="issue_a" issues={reference.issues} value={form.issue_a} disabled={locked} onChange={(v) => set('issue_a', v)} />
            </Field>
            <Field label="Issue 2" htmlFor="issue_b">
              <IssueSelect id="issue_b" issues={reference.issues} value={form.issue_b} disabled={locked} onChange={(v) => set('issue_b', v)} />
            </Field>
            <Field label="Issue 3" htmlFor="issue_c">
              <IssueSelect id="issue_c" issues={reference.issues} value={form.issue_c} disabled={locked} onChange={(v) => set('issue_c', v)} />
            </Field>
          </div>

          <div className="grid full" style={{ marginTop: 14 }}>
            <Field label="Complaint description" htmlFor="description">
              <textarea
                id="description"
                value={form.description}
                placeholder="Describe the complaint in detail... (max 20,000 characters)"
                maxLength={20_000}
                disabled={locked}
                onChange={(e) => set('description', e.target.value)}
              />
            </Field>
            <Field label="Desired outcome" htmlFor="desired_outcome">
              <textarea
                id="desired_outcome"
                value={form.desired_outcome ?? ''}
                placeholder="What resolution is the customer seeking? (max 20,000 characters)"
                maxLength={20_000}
                disabled={locked}
                onChange={(e) => set('desired_outcome', e.target.value)}
              />
            </Field>
          </div>

          {!locked && (
            <div style={{ marginTop: 16 }}>
              <button type="submit" className="btn primary" disabled={mutation.isPending}>
                <Save size={16} aria-hidden /> {mutation.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          )}
        </div>
      </form>

      <div className="card">
        <h2 className="section-title">Complaint information</h2>
        <dl className="info-grid">
          <div>
            <dt>Channel</dt>
            <dd>{complaint.channel}</dd>
          </div>
          <div>
            <dt>Complainant type</dt>
            <dd>{reference.complainantTypes.find((t) => t.code === complaint.complainant_type)?.label ?? '—'}</dd>
          </div>
          <div>
            <dt>Gender</dt>
            <dd>{reference.genders.find((g) => g.code === complaint.complainant_gender)?.label ?? '—'}</dd>
          </div>
          <div>
            <dt>Made by</dt>
            <dd>{complaint.direct_or_rep}{complaint.representative_name ? ` — ${complaint.representative_name}` : ''}</dd>
          </div>
          <div>
            <dt>Issue 1</dt>
            <dd>{issueLabel(reference.issues, complaint.issue_a)}</dd>
          </div>
          <div>
            <dt>Days to resolve</dt>
            <dd>{complaint.days_to_resolve ?? '—'}</dd>
          </div>
          <div>
            <dt>Last updated</dt>
            <dd>{formatDateTime(complaint.updated_at)} by {complaint.updated_by}</dd>
          </div>
        </dl>
      </div>

      <div className="card">
        <h2 className="section-title">Case notes</h2>

        <h3 className="section-title" style={{ fontSize: 12 }}>Case outcome</h3>
        <form onSubmit={handleSave}>
          <div className="grid full">
            <Field label="Outcome 1" htmlFor="outcome_a">
              <OutcomeSelect id="outcome_a" outcomes={reference.outcomes} value={form.outcome_a} disabled={locked} onChange={(v) => set('outcome_a', v)} />
            </Field>
            <Field label="Outcome 2" htmlFor="outcome_b">
              <OutcomeSelect id="outcome_b" outcomes={reference.outcomes} value={form.outcome_b} disabled={locked} onChange={(v) => set('outcome_b', v)} />
            </Field>
            <Field label="Outcome 3" htmlFor="outcome_c">
              <OutcomeSelect id="outcome_c" outcomes={reference.outcomes} value={form.outcome_c} disabled={locked} onChange={(v) => set('outcome_c', v)} />
            </Field>
          </div>
          <div className="grid" style={{ marginTop: 14 }}>
            <Field label="Monetary remedy (AUD)" htmlFor="monetary_remedy" hint="Optional — dollar value of any compensation offered">
              <input
                id="monetary_remedy"
                type="number"
                min="0"
                step="0.01"
                value={form.monetary_remedy ?? ''}
                disabled={locked}
                onChange={(e) => set('monetary_remedy', e.target.value)}
              />
            </Field>
          </div>
          <div className="grid full" style={{ marginTop: 14 }}>
            <Field label="Material findings & facts" htmlFor="material_findings" hint="Root cause and findings of the investigation">
              <textarea
                id="material_findings"
                value={form.material_findings ?? ''}
                placeholder="What did the investigation find? (max 20,000 characters)"
                maxLength={20_000}
                disabled={locked}
                onChange={(e) => set('material_findings', e.target.value)}
              />
            </Field>
            <Field label="Decision & remediation" htmlFor="decision_remediation" hint="The final decision made on this complaint">
              <textarea
                id="decision_remediation"
                value={form.decision_remediation ?? ''}
                placeholder="What was decided, and what remediation follows? (max 20,000 characters)"
                maxLength={20_000}
                disabled={locked}
                onChange={(e) => set('decision_remediation', e.target.value)}
              />
            </Field>
          </div>
          {!locked && (
            <div style={{ marginTop: 16 }}>
              <button type="submit" className="btn primary" disabled={mutation.isPending}>
                <Save size={16} aria-hidden /> {mutation.isPending ? 'Saving…' : 'Save changes'}
              </button>
            </div>
          )}
        </form>

        <h3 className="section-title" style={{ fontSize: 12, marginTop: 24 }}>Notes</h3>
        <NoteComposer
          complaintId={complaintId}
          onAdded={() => void queryClient.invalidateQueries({ queryKey: ['complaint', complaintId] })}
          onError={(message) => show(message, 'error')}
        />
        {notes.length === 0 ? (
          <p className="faint" style={{ fontSize: 12 }}>No notes recorded yet.</p>
        ) : (
          notes.map((note) => (
            <div key={note.id} className="log-row">
              <span className="log-time">{formatDateTime(note.created_at)}</span>
              <span className="log-actor">{note.author_email}</span>
              <span style={{ flex: 1 }}>{note.note}</span>
            </div>
          ))
        )}
      </div>

      <div className="card">
        <h2 className="section-title">Attachments</h2>
        <AttachmentUploader
          complaintId={complaintId}
          onUploaded={() => void queryClient.invalidateQueries({ queryKey: ['complaint', complaintId] })}
          onError={(message) => show(message, 'error')}
        />
        {attachments.length === 0 ? (
          <p className="faint" style={{ fontSize: 12 }}>No files attached yet.</p>
        ) : (
          attachments.map((attachment) => (
            <AttachmentRow
              key={attachment.id}
              complaintId={complaintId}
              attachment={attachment}
              canDelete={permissions.can_edit && !isClosed}
              onError={(message) => show(message, 'error')}
              onDeleted={() => {
                void queryClient.invalidateQueries({ queryKey: ['complaint', complaintId] });
                show('Attachment deleted');
              }}
              onNoteSaved={() => {
                void queryClient.invalidateQueries({ queryKey: ['complaint', complaintId] });
                show('Note saved');
              }}
            />
          ))
        )}
      </div>

      <div className="card">
        <h2 className="section-title">Activity log</h2>
        {audit.length === 0 ? (
          <p className="faint" style={{ fontSize: 12 }}>No history recorded.</p>
        ) : (
          audit.map((entry) => (
            <div key={entry.id} className="log-row">
              <span className="log-time">{formatDateTime(entry.created_at)}</span>
              <span className="log-actor">{entry.actor_email}</span>
              <span style={{ flex: 1 }}>
                {entry.action}
                {entry.field_changed ? ` — ${entry.field_changed}` : ''}
                {entry.new_value
                  ? `: ${entry.old_value ? formatAuditValue(entry.field_changed, entry.old_value) : '(empty)'} → ${formatAuditValue(entry.field_changed, entry.new_value)}`
                  : ''}
              </span>
            </div>
          ))
        )}
      </div>

      {toastElement}
    </div>
  );
}
